
export default function PricingSection() {
  const pricingPlans = [
    {
      name: "Recreational",
      price: "$100-120",
      period: "per month",
      description: "Perfect for beginners and casual players",
      features: [
        "2 training sessions per week",
        "Basic skill development",
        "Fun-focused environment",
        "Equipment provided",
        "End of season tournament",
        "Player development report"
      ],
      popular: false,
      programValue: "recreational"
    },
    {
      name: "Competitive",
      price: "$150-180",
      period: "per month",
      description: "For players ready to take their game to the next level",
      features: [
        "3 training sessions per week",
        "Advanced skill development",
        "League participation",
        "Professional coaching",
        "Fitness and conditioning",
        "Tournament opportunities",
        "Individual player analysis",
        "Goalkeeper training included"
      ],
      popular: true,
      programValue: "competitive"
    },
    {
      name: "Elite Training",
      price: "$200",
      period: "per month",
      description: "Intensive program for serious players",
      features: [
        "4 training sessions per week",
        "Elite level coaching",
        "Multiple league participation",
        "Individual training sessions",
        "Video analysis",
        "Nutrition guidance",
        "College recruitment support",
        "Professional pathway guidance",
        "Mental performance coaching"
      ],
      popular: false,
      programValue: "elite"
    }
  ];

  const handleChoosePlan = (programType: string) => {
    // Scroll to registration form
    const registrationForm = document.getElementById('soccer-registration-form');
    if (registrationForm) {
      registrationForm.scrollIntoView({ 
        behavior: 'smooth',
        block: 'start'
      });
      
      // Wait for scroll to complete, then pre-select program
      setTimeout(() => {
        const programSelect = document.getElementById('program') as HTMLSelectElement;
        if (programSelect) {
          // Map program types to actual form values
          let programValue = '';
          
          switch (programType) {
            case 'recreational':
              // Default to U10 recreational for general selection
              programValue = 'recreational-u10';
              break;
            case 'competitive':
              // Default to U14 competitive for general selection
              programValue = 'competitive-u14';
              break;
            case 'elite':
              // Default to U18 elite for general selection
              programValue = 'elite-u18';
              break;
          }
          
          if (programValue) {
            programSelect.value = programValue;
            // Trigger change event to update form state
            const changeEvent = new Event('change', { bubbles: true });
            programSelect.dispatchEvent(changeEvent);
          }
        }
      }, 800);
    }
  };

  return (
    <section className="py-16 px-4 bg-white">
      <div className="max-w-6xl mx-auto">
        <div className="text-center mb-12">
          <h2 className="text-4xl font-bold text-gray-900 mb-4">Program Pricing</h2>
          <p className="text-xl text-gray-600 max-w-3xl mx-auto">
            Choose the program that best fits your player's goals and commitment level. All programs include professional coaching and development.
          </p>
        </div>

        <div className="grid grid-cols-1 md:grid-cols-3 gap-8">
          {pricingPlans.map((plan, index) => (
            <div
              key={index}
              className={`relative bg-white rounded-lg shadow-lg border-2 p-8 ${
                plan.popular ? 'border-purple-500 transform scale-105' : 'border-gray-200'
              }`}
            >
              {plan.popular && (
                <div className="absolute -top-4 left-1/2 transform -translate-x-1/2">
                  <span className="bg-purple-500 text-white px-4 py-2 rounded-full text-sm font-semibold whitespace-nowrap">
                    Most Popular
                  </span>
                </div>
              )}

              <div className="text-center mb-6">
                <h3 className="text-2xl font-bold text-gray-900 mb-2">{plan.name}</h3>
                <p className="text-gray-600 mb-4">{plan.description}</p>
                <div className="mb-4">
                  <span className="text-4xl font-bold text-purple-500">{plan.price}</span>
                  <span className="text-gray-600 ml-2">{plan.period}</span>
                </div>
              </div>

              <ul className="space-y-3 mb-8">
                {plan.features.map((feature, featureIndex) => (
                  <li key={featureIndex} className="flex items-start">
                    <i className="ri-check-line text-purple-500 text-lg mr-3 mt-0.5 w-5 h-5 flex items-center justify-center"></i>
                    <span className="text-gray-700">{feature}</span>
                  </li>
                ))}
              </ul>

              <button
                onClick={() => handleChoosePlan(plan.programValue)}
                className={`w-full py-3 px-6 rounded-lg font-semibold transition-colors whitespace-nowrap cursor-pointer ${
                  plan.popular
                    ? 'bg-purple-600 hover:bg-purple-700 text-white'
                    : 'border-2 border-purple-500 text-purple-500 hover:bg-purple-500 hover:text-white'
                }`}
              >
                Choose {plan.name}
              </button>
            </div>
          ))}
        </div>

        <div className="mt-12 text-center">
          <div className="bg-gray-50 rounded-lg p-8">
            <h3 className="text-2xl font-bold text-gray-900 mb-4">Additional Information</h3>
            <div className="grid grid-cols-1 md:grid-cols-2 gap-6 text-left">
              <div>
                <h4 className="font-semibold text-gray-900 mb-2">Registration Fee</h4>
                <p className="text-gray-600">One-time registration fee of $80 per player (includes jersey and training materials)</p>
              </div>
              <div>
                <h4 className="font-semibold text-gray-900 mb-2">Family Discounts</h4>
                <p className="text-gray-600">10% discount for second child, 15% discount for third child and beyond</p>
              </div>
              <div>
                <h4 className="font-semibold text-gray-900 mb-2">Payment Options</h4>
                <p className="text-gray-600">Monthly, quarterly (5% discount), or annual (10% discount) payment plans available</p>
              </div>
              <div>
                <h4 className="font-semibold text-gray-900 mb-2">Summer Camps</h4>
                <p className="text-gray-600">Weekly summer camp programs available at $300 per week with specialized training</p>
              </div>
            </div>
          </div>
        </div>
      </div>
    </section>
  );
}
